import React from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { quickLinks, contactInfo } from '../../data/mock';

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
          {/* Brand Section */}
          <div className="space-y-4 sm:space-y-6 sm:col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                <Stethoscope className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <span className="text-xl sm:text-2xl font-bold">
                <span className="text-white">Doktor</span>{' '}
                <span className="text-teal-400">Gigi</span>
              </span>
            </Link>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
              Redefining kidney care through expert telemedicine. We provide clear guidance,
              transparent pricing, and world-class specialists for kidney stone management.
            </p>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-800 rounded-full flex items-center justify-center">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400" />
              </div>
              <a
                href={contactInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full px-4 sm:px-6 py-1.5 sm:py-2 font-medium flex items-center gap-2 text-xs sm:text-sm">
                  <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                  WhatsApp Support
                </Button>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[10px] sm:text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4 sm:mb-6">
              Quick Links
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-teal-400 transition-colors text-xs sm:text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-[10px] sm:text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4 sm:mb-6">
              Contact
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-center gap-2 sm:gap-3">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-teal-400" />
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-teal-400 hover:text-teal-300 transition-colors text-xs sm:text-sm"
                >
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-2 sm:gap-3">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-gray-300 hover:text-teal-400 transition-colors text-xs sm:text-sm"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-2 sm:gap-3">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-300 text-xs sm:text-sm">{contactInfo.availability}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-slate-800 mt-8 pt-8">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
            <Link
              to="/terms-conditions"
              className="text-gray-400 hover:text-teal-400 transition-colors text-xs sm:text-sm"
            >
              Terms & Conditions
            </Link>
            <Link
              to="/medical-disclaimer"
              className="text-gray-400 hover:text-teal-400 transition-colors text-xs sm:text-sm"
            >
              Medical Disclaimer
            </Link>
            <Link
              to="/privacy-policy"
              className="text-gray-400 hover:text-teal-400 transition-colors text-xs sm:text-sm"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
      
      {/* Powered by Loms */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-4 sm:py-6">
          <p className="text-center text-gray-500 text-xs sm:text-sm">
            Powered by <span className="text-teal-400 font-medium">Loms</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
